import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { useTranslation } from 'react-i18next';
import {
  FaCheckCircle, FaTimesCircle, FaRocket, FaChartLine, FaBuilding,
  FaIndustry, FaFlask, FaClipboardList, FaChartPie,
  FaArrowRight, FaStar, FaHeadset, FaUserTie,
  FaPuzzlePiece, FaSitemap, FaChevronDown, FaShieldAlt, FaCheck
} from 'react-icons/fa';
import theme from '../../theme';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 120px 5% 80px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${theme.colors.primary} 100%);
  text-align: center;
  color: white;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
    border-radius: 50%;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 850px;
  margin: 0 auto;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 2rem;
  border: 1px solid rgba(255,255,255,0.15);
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.15;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2.5rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 2rem; }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto 2rem;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1rem; }
`;

const SetupNote = styled.div`
  text-align: center;
  padding: 2rem 5%;
  background: ${theme.colors.lightGray};

  p {
    font-size: 0.85rem;
    color: ${theme.colors.darkGray};
    line-height: 1.8;
    max-width: 550px;
    margin: 0 auto;
    font-style: italic;
  }
`;

/* === BILLING TOGGLE === */

const BillingToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
`;

const BillingToggle = styled.div`
  display: inline-flex;
  background: white;
  border-radius: 50px;
  padding: 4px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
`;

const BillingOption = styled.button`
  position: relative;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${props => props.$active ? css`
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, #1a1f3a 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  ` : css`
    background: transparent;
    color: ${theme.colors.darkGray};
    &:hover { color: ${theme.colors.secondary}; }
  `}

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }
`;

const SaveBadge = styled.span`
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.15rem 0.5rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${props => props.$active ? 'rgba(255,255,255,0.2)' : 'rgba(245, 158, 11, 0.15)'};
  color: ${props => props.$active ? 'white' : '#b45309'};
`;

/* === PRICING CARDS === */

const PricingSection = styled.section`
  padding: 80px 5%;
  background: ${theme.colors.lightGray};
  position: relative;
  margin-top: -40px;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  align-items: stretch;

  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    max-width: 500px;
    gap: 2.5rem;
  }
`;

const PricingCard = styled.div`
  position: relative;
  background: ${props => props.$featured ? '#F7F7F9' : 'white'};
  border-radius: ${props => props.$featured ? '24px' : '20px'};
  padding: ${props => props.$featured ? '3rem 2rem' : '2.5rem 2rem'};
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 1;

  ${props => props.$featured ? `
    border: 2px solid #8B0000;
    transform: scale(1.05);
    box-shadow: 0 25px 60px rgba(139, 0, 0, 0.12), 0 8px 24px rgba(0,0,0,0.06);
  ` : props.$enterprise ? `
    border: 2.5px solid #4a4a55;
    box-shadow: 0 10px 40px rgba(0,0,0,0.06);
  ` : `
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  `}

  &:hover {
    transform: ${props => props.$featured ? 'scale(1.07)' : 'translateY(-10px)'};
    box-shadow: ${props => props.$featured
      ? '0 30px 70px rgba(139, 0, 0, 0.18), 0 10px 30px rgba(0,0,0,0.08)'
      : '0 25px 70px rgba(0,0,0,0.15)'};
  }

  @media (max-width: ${theme.breakpoints.desktop}) {
    transform: scale(1);
    &:hover { transform: translateY(-10px); }
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: #111111;
  color: white;
  padding: 0.5rem 1.5rem 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  border-left: 3px solid #8B0000;
`;

const CardIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.8rem;
  color: white;
  background: ${props => props.$gradient || `linear-gradient(135deg, #0f172a 0%, ${theme.colors.primary} 100%)`};
  box-shadow: 0 8px 25px ${props => props.$shadowColor || 'rgba(15, 23, 42, 0.3)'};
  animation: ${float} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.25rem;
  color: ${props => props.$enterprise ? '#3a3a44' : theme.colors.secondary};
`;

const TargetAudience = styled.p`
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.colors.darkGray};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const PackageTagline = styled.p`
  text-align: center;
  font-size: 0.82rem;
  color: #64748b;
  font-style: italic;
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const PriceTag = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const PriceLabel = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.darkGray};
  margin-bottom: 0.25rem;
`;

const PriceSuffix = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.darkGray};
  margin-top: 0.25rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
`;

const PriceAmount = styled.span`
  font-size: 2.8rem;
  font-weight: 800;
  color: ${props => props.$featured ? '#8B0000' : theme.colors.primary};
  line-height: 1;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 2.2rem; }
`;

const PricePeriod = styled.span`
  font-size: 0.9rem;
  color: ${theme.colors.darkGray};
`;

const OriginalPrice = styled.span`
  font-size: 1rem;
  color: #b0b0b0;
  text-decoration: line-through;
  margin-left: 0.5rem;
`;

const BillingNote = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.darkGray};
  margin-top: 0.35rem;
`;

const OnboardingFee = styled.div`
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.$featured ? '#6b4040' : theme.colors.darkGray};
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
  padding: 0.35rem 0.75rem;
  background: ${props => props.$featured
    ? 'rgba(139, 0, 0, 0.06)'
    : 'rgba(102, 126, 234, 0.04)'};
  border-radius: 6px;
  display: inline-block;
  width: 100%;
`;

const PlanDescription = styled.p`
  text-align: center;
  color: ${theme.colors.darkGray};
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SuitableFor = styled.div`
  text-align: center;
  font-size: 0.82rem;
  font-style: italic;
  color: ${theme.colors.darkGray};
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.$featured
    ? 'rgba(139, 0, 0, 0.04)'
    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.04), rgba(118, 75, 162, 0.04))'};
  border-radius: 10px;
  border-left: 3px solid ${props => props.$featured ? '#8B0000' : theme.colors.primary};
  line-height: 1.5;
`;

const PreferredBadge = styled.div`
  text-align: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: #8B0000;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const IncludesBadge = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: ${props => props.$featured
    ? 'rgba(139, 0, 0, 0.08)'
    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08))'};
  border: 1px solid ${props => props.$featured ? 'rgba(139, 0, 0, 0.2)' : 'rgba(102, 126, 234, 0.15)'};
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${props => props.$featured ? '#8B0000' : theme.colors.primary};
`;

const Divider = styled.div`
  height: 1px;
  background: ${props => props.$featured
    ? 'linear-gradient(90deg, transparent, rgba(139, 0, 0, 0.15), transparent)'
    : 'linear-gradient(90deg, transparent, #e5e7eb, transparent)'};
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  flex: 1;
`;

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${theme.colors.secondary};

  svg {
    flex-shrink: 0;
    width: 17px;
    height: 17px;
    color: ${props => props.$featured ? '#8B0000' : theme.colors.primary};
    margin-top: 2px;
  }
`;

const ExcludedFeature = styled(Feature)`
  opacity: 0.5;
  svg { color: #d1d5db; }
`;

const PlanButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-decoration: none;

  ${props => props.$featured ? `
    background: #8B0000;
    color: white;
    font-weight: 800;
    box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);
    &:hover {
      background: #6b0000;
      box-shadow: 0 12px 35px rgba(139, 0, 0, 0.45);
      transform: translateY(-2px);
      color: white;
    }
  ` : props.$enterprise ? `
    background: #4a4a55;
    color: white;
    border: none;
    &:hover {
      background: #3a3a44;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(74, 74, 85, 0.3);
      color: white;
    }
  ` : `
    background: white;
    color: #1a1a1a;
    border: 2px solid #1a1a1a;
    &:hover {
      background: #1a1a1a;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
  `}

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

/* === PRODUCTION ADD-ON === */

const AddOnSection = styled.section`
  padding: 80px 5%;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary}, #f59e0b, ${theme.colors.primary});
  }
`;

const AddOnContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const AddOnTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05));
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #b45309;
  letter-spacing: 2px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  svg { width: 14px; height: 14px; }
`;

const AddOnTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  max-width: 700px;
  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 1.75rem; }
`;

const AddOnSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.darkGray};
  line-height: 1.7;
  max-width: 650px;
  margin-bottom: 3rem;
`;

const AddOnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;

const AddOnFeatureCard = styled.div`
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 40px rgba(0,0,0,0.08);
    border-color: #f59e0b;
  }
`;

const AddOnFeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(245, 158, 11, 0.06));
  svg { width: 26px; height: 26px; color: #b45309; }
`;

const AddOnFeatureTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const AddOnFeatureDesc = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.darkGray};
  line-height: 1.6;
`;

const AddOnCTA = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 20px;
  padding: 3rem;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  &:before {
    content: '';
    position: absolute;
    top: -30%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
  }
`;

const AddOnCTAText = styled.div`
  position: relative;
  z-index: 1;

  h3 {
    font-size: 1.5rem;
    font-weight: 800;
    color: white;
    margin-bottom: 0.5rem;
  }

  p {
    color: rgba(255,255,255,0.7);
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const AddOnCTAButton = styled(Link)`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(245, 158, 11, 0.5);
    color: white;
  }
  svg { transition: transform 0.3s ease; }
  &:hover svg { transform: translateX(4px); }
`;

/* === COMPARISON TABLE === */

const ComparisonSection = styled.section`
  padding: 80px 5%;
  background: ${theme.colors.lightGray};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 0.75rem;
  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 1.75rem; }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: ${theme.colors.darkGray};
  font-size: 1.1rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const TableWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  overflow-x: auto;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 700px;

  thead tr {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  }

  thead th {
    padding: 1.25rem 1rem;
    color: white;
    font-weight: 700;
    font-size: 0.95rem;
    text-align: center;
    letter-spacing: 0.5px;
    &:first-child { text-align: left; padding-left: 1.5rem; }
  }

  tbody tr {
    transition: background 0.2s ease;
    &:hover { background: rgba(15, 23, 42, 0.03); }
    &:not(:last-child) td { border-bottom: 1px solid #f1f5f9; }
  }

  tbody td {
    padding: 1rem;
    text-align: center;
    font-size: 0.9rem;
    color: ${theme.colors.secondary};
    &:first-child { text-align: left; padding-left: 1.5rem; font-weight: 500; }
  }
`;

const CheckMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.04));
  color: ${theme.colors.primary};
  svg { width: 14px; height: 14px; }
`;

const DashMark = styled.span`
  display: inline-block;
  width: 20px;
  height: 2px;
  background: #d1d5db;
  border-radius: 2px;
`;

/* === GUARANTEE === */

const GuaranteeSection = styled.section`
  padding: 80px 5%;
  background: white;
`;

const GuaranteeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; }
`;

const GuaranteeCard = styled.div`
  text-align: center;
  padding: 2.5rem 2rem;
  background: ${theme.colors.lightGray};
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 35px rgba(0,0,0,0.08);
  }

  svg { width: 40px; height: 40px; color: ${theme.colors.primary}; margin-bottom: 1rem; }
  h3 { font-size: 1.1rem; margin-bottom: 0.75rem; color: ${theme.colors.secondary}; font-weight: 700; }
  p { color: ${theme.colors.darkGray}; font-size: 0.9rem; line-height: 1.6; }
`;

/* === CTA === */

const CTASection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${theme.colors.primary} 100%);
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -30%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.75rem; }
`;

const CTAText = styled.p`
  font-size: 1.15rem;
  opacity: 0.9;
  line-height: 1.7;
  margin-bottom: 2.5rem;
`;

const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  flex-wrap: wrap;
`;

const CTAPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 3rem;
  background: white;
  color: ${theme.colors.primary};
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  text-decoration: none;

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 40px rgba(0,0,0,0.3);
    color: ${theme.colors.primary};
  }
  svg { transition: transform 0.3s ease; }
  &:hover svg { transform: translateX(4px); }
`;

const CTASecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 3rem;
  background: transparent;
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 50px;
  border: 2px solid rgba(255,255,255,0.4);
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: rgba(255,255,255,0.1);
    border-color: rgba(255,255,255,0.7);
    transform: translateY(-3px);
    color: white;
  }
`;

/* === ROI BLOCK === */

const ROISection = styled.section`
  padding: 80px 5%;
  background: white;
`;

const ROIContainer = styled.div`
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
`;

const ROITitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.4rem; }
`;

const ROISubtitle = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.darkGray};
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const ROIList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 auto 2.5rem;
  max-width: 420px;
  text-align: left;
`;

const ROIItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  font-size: 0.95rem;
  color: ${theme.colors.secondary};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: #8B0000;
  }
`;

const ROICTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 2rem;
  background: white;
  color: #1a1a1a;
  border: 2px solid #1a1a1a;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #1a1a1a;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

/* === FAQ SECTION === */

const FAQSection = styled.section`
  padding: 80px 5%;
  background: ${theme.colors.lightGray};
`;

const FAQContainer = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

const FAQTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 3rem;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.5rem; }
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.$open ? 'rgba(139, 0, 0, 0.15)' : '#e5e7eb'};
  transition: border-color 0.3s ease;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
  transition: color 0.3s ease;

  &:hover { color: #8B0000; }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${theme.colors.darkGray};
    transition: transform 0.3s ease;
    transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FAQAnswer = styled.div`
  max-height: ${props => props.$open ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding: ${props => props.$open ? '0 1.5rem 1.25rem' : '0 1.5rem'};

  p {
    font-size: 0.9rem;
    color: ${theme.colors.darkGray};
    line-height: 1.7;
  }
`;

/* === BOTTOM CTA === */

const BottomCTASection = styled.section`
  padding: 80px 5%;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -40%;
    right: -15%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(139, 0, 0, 0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const BottomCTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
`;

const BottomCTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 2rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.5rem; }
`;

const BottomCTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.15rem 2.5rem;
  background: #8B0000;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);

  &:hover {
    background: #6b0000;
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(139, 0, 0, 0.45);
    color: white;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

const BottomCTATrust = styled.p`
  margin-top: 1.25rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const PricingPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);

  const f = (key) => t(`pricing.features.${key}`);

  const basePrices = { starter: 390, growth: 690, enterprise: 990 };

  const getPrice = (base) => {
    if (billingPeriod === 'annual') return Math.round(base * 0.9);
    if (billingPeriod === 'biennial') return Math.round(base * 0.85);
    return base;
  };

  const formatPrice = (base) => {
    const price = getPrice(base);
    const isEnterprise = base === 990;
    return `€${price}${isEnterprise ? '+' : ''}`;
  };

  const packages = [
    {
      name: 'Starter',
      target: t('pricing.packages.starter.target'),
      tagline: t('pricing.packages.starter.tagline'),
      basePrice: basePrices.starter,
      onboardingFee: t('pricing.packages.starter.onboardingFee'),
      description: t('pricing.packages.starter.description'),
      suitableFor: t('pricing.packages.starter.suitableFor'),
      icon: <FaRocket />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      shadowColor: 'rgba(102, 126, 234, 0.3)',
      delay: '0s',
      featured: false,
      includesLabel: null,
      preferredNote: null,
      featureKeys: [
        'b2bPanel', 'mobileApp', 'orderManagement',
        'customPricing', 'pushNotifications', 'multiLangCurrency', 'mailNotifications',
      ],
      excludedKeys: [],
      buttonText: t('pricing.demoButton'),
      buttonLink: `/${lang}/demo`,
    },
    {
      name: 'Growth',
      target: t('pricing.packages.growth.target'),
      tagline: t('pricing.packages.growth.tagline'),
      basePrice: basePrices.growth,
      onboardingFee: t('pricing.packages.growth.onboardingFee'),
      description: t('pricing.packages.growth.description'),
      suitableFor: t('pricing.packages.growth.suitableFor'),
      icon: <FaChartLine />,
      gradient: 'linear-gradient(135deg, #8B0000 0%, #b91c1c 100%)',
      shadowColor: 'rgba(139, 0, 0, 0.3)',
      delay: '0.5s',
      featured: true,
      includesLabel: t('pricing.packages.growth.includesAll'),
      preferredNote: t('pricing.packages.growth.preferredNote'),
      featureKeys: [
        'erpIntegration', 'warehouseManagement', 'barcodeApp',
        'invoiceStructure', 'purchaseOrders', 'dataTransfer', 'advancedReporting',
      ],
      excludedKeys: [],
      buttonText: t('pricing.demoButton'),
      buttonLink: `/${lang}/demo`,
    },
    {
      name: 'Enterprise',
      target: t('pricing.packages.enterprise.target'),
      tagline: t('pricing.packages.enterprise.tagline'),
      basePrice: basePrices.enterprise,
      onboardingFee: t('pricing.packages.enterprise.onboardingFee'),
      description: t('pricing.packages.enterprise.description'),
      suitableFor: null,
      icon: <FaBuilding />,
      gradient: 'linear-gradient(135deg, #4a4a55 0%, #6b6b78 100%)',
      shadowColor: 'rgba(74, 74, 85, 0.3)',
      delay: '1s',
      featured: false,
      enterprise: true,
      includesLabel: t('pricing.packages.enterprise.includesAll'),
      preferredNote: null,
      featureKeys: [
        'multiWarehouse', 'apiCustomIntegration', 'advancedAuth',
        'prioritySupport', 'sla',
      ],
      excludedKeys: [],
      buttonText: t('pricing.enterpriseButton'),
      buttonLink: `/${lang}/demo`,
    },
  ];

  const productionFeatures = [
    { icon: <FaFlask />, titleKey: 'recipeBased', descKey: 'recipeBasedDesc' },
    { icon: <FaSitemap />, titleKey: 'bomManagement', descKey: 'bomManagementDesc' },
    { icon: <FaClipboardList />, titleKey: 'rawMaterialTracking', descKey: 'rawMaterialTrackingDesc' },
    { icon: <FaChartPie />, titleKey: 'productionPlanning', descKey: 'productionPlanningDesc' },
    { icon: <FaChartLine />, titleKey: 'costAnalysis', descKey: 'costAnalysisDesc' },
    { icon: <FaPuzzlePiece />, titleKey: 'lotSerial', descKey: 'lotSerialDesc' },
  ];

  const comparisonRows = [
    { key: 'b2bPanel', starter: true, growth: true, enterprise: true },
    { key: 'mobileApp', starter: true, growth: true, enterprise: true },
    { key: 'orderManagement', starter: true, growth: true, enterprise: true },
    { key: 'customPricing', starter: true, growth: true, enterprise: true },
    { key: 'pushNotifications', starter: true, growth: true, enterprise: true },
    { key: 'multiLangCurrency', starter: true, growth: true, enterprise: true },
    { key: 'mailNotifications', starter: true, growth: true, enterprise: true },
    { key: 'erpIntegration', starter: false, growth: true, enterprise: true },
    { key: 'warehouseManagement', starter: false, growth: true, enterprise: true },
    { key: 'barcodeApp', starter: false, growth: true, enterprise: true },
    { key: 'invoiceStructure', starter: false, growth: true, enterprise: true },
    { key: 'purchaseOrders', starter: false, growth: true, enterprise: true },
    { key: 'dataTransfer', starter: false, growth: true, enterprise: true },
    { key: 'advancedReporting', starter: false, growth: true, enterprise: true },
    { key: 'multiWarehouse', starter: false, growth: false, enterprise: true },
    { key: 'apiCustomIntegration', starter: false, growth: false, enterprise: true },
    { key: 'advancedAuth', starter: false, growth: false, enterprise: true },
    { key: 'prioritySupport', starter: false, growth: false, enterprise: true },
    { key: 'sla', starter: false, growth: false, enterprise: true },
  ];

  const faqItems = [
    { q: t('pricing.faq.q1'), a: t('pricing.faq.a1') },
    { q: t('pricing.faq.q2'), a: t('pricing.faq.a2') },
    { q: t('pricing.faq.q3'), a: t('pricing.faq.a3') },
    { q: t('pricing.faq.q4'), a: t('pricing.faq.a4') },
    { q: t('pricing.faq.q5'), a: t('pricing.faq.a5') },
    { q: t('pricing.faq.q6'), a: t('pricing.faq.a6') },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <>
      <SEO titleKey="pricing.seo.title" descriptionKey="pricing.seo.description" />

      <PageContainer>
        {/* HERO */}
        <HeroSection>
          <HeroContent>
            <HeroBadge><FaStar /> {t('pricing.hero.badge')}</HeroBadge>
            <HeroTitle>{t('pricing.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('pricing.hero.subtitle')}</HeroSubtitle>
          </HeroContent>
        </HeroSection>

        {/* PACKAGES */}
        <PricingSection>
          <BillingToggleWrapper>
            <BillingToggle>
              <BillingOption $active={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')}>
                {t('pricing.billing.monthly')}
              </BillingOption>
              <BillingOption $active={billingPeriod === 'annual'} onClick={() => setBillingPeriod('annual')}>
                {t('pricing.billing.annual')}
                <SaveBadge $active={billingPeriod === 'annual'}>{t('pricing.billing.annualSave')}</SaveBadge>
              </BillingOption>
              <BillingOption $active={billingPeriod === 'biennial'} onClick={() => setBillingPeriod('biennial')}>
                {t('pricing.billing.biennial')}
                <SaveBadge $active={billingPeriod === 'biennial'}>{t('pricing.billing.biennialSave')}</SaveBadge>
              </BillingOption>
            </BillingToggle>
          </BillingToggleWrapper>

          <PricingGrid>
            {packages.map((pkg, index) => (
              <PricingCard key={index} $featured={pkg.featured} $enterprise={pkg.enterprise}>
                {pkg.featured && (
                  <PopularBadge><FaStar /> {t('pricing.popularBadge')}</PopularBadge>
                )}

                <CardIcon $gradient={pkg.gradient} $shadowColor={pkg.shadowColor} $delay={pkg.delay}>
                  {pkg.icon}
                </CardIcon>

                <PlanName $featured={pkg.featured} $enterprise={pkg.enterprise}>{pkg.name}</PlanName>
                <TargetAudience>{pkg.target}</TargetAudience>
                <PackageTagline>{pkg.tagline}</PackageTagline>

                <PriceTag>
                  {t('pricing.pricePrefix') && (
                    <PriceLabel>{t('pricing.pricePrefix')}</PriceLabel>
                  )}
                  <PriceRow>
                    <PriceAmount $featured={pkg.featured}>{formatPrice(pkg.basePrice)}</PriceAmount>
                    <PricePeriod>{t('pricing.billing.perMonth')}</PricePeriod>
                    {billingPeriod !== 'monthly' && (
                      <OriginalPrice>€{pkg.basePrice}</OriginalPrice>
                    )}
                  </PriceRow>
                  {t('pricing.priceSuffix') && t('pricing.priceSuffix') !== 'pricing.priceSuffix' && (
                    <PriceSuffix>{t('pricing.priceSuffix')}</PriceSuffix>
                  )}
                  {billingPeriod !== 'monthly' && (
                    <BillingNote>
                      {billingPeriod === 'annual'
                        ? t('pricing.billing.annualNote')
                        : t('pricing.billing.biennialNote')}
                    </BillingNote>
                  )}
                  <OnboardingFee $featured={pkg.featured}>{pkg.onboardingFee}</OnboardingFee>
                </PriceTag>

                <PlanDescription>{pkg.description}</PlanDescription>

                {pkg.preferredNote && (
                  <PreferredBadge>
                    <FaStar /> {pkg.preferredNote}
                  </PreferredBadge>
                )}

                {pkg.includesLabel && (
                  <IncludesBadge $featured={pkg.featured}>{pkg.includesLabel}</IncludesBadge>
                )}

                <Divider $featured={pkg.featured} />

                <FeatureList>
                  {pkg.featureKeys.map((key, idx) => (
                    <Feature key={idx} $featured={pkg.featured}>
                      <FaCheckCircle />
                      <span>{f(key)}</span>
                    </Feature>
                  ))}
                  {pkg.excludedKeys.map((key, idx) => (
                    <ExcludedFeature key={`ex-${idx}`}>
                      <FaTimesCircle />
                      <span>{f(key)}</span>
                    </ExcludedFeature>
                  ))}
                </FeatureList>

                {pkg.suitableFor && (
                  <SuitableFor $featured={pkg.featured}>{pkg.suitableFor}</SuitableFor>
                )}

                <PlanButton to={pkg.buttonLink} $featured={pkg.featured} $enterprise={pkg.enterprise}>
                  {pkg.buttonText}
                  <FaArrowRight />
                </PlanButton>
              </PricingCard>
            ))}
          </PricingGrid>
        </PricingSection>

        {/* PRODUCTION ADD-ON */}
        <AddOnSection>
          <AddOnContainer>
            <AddOnTag><FaIndustry /> {t('pricing.production.tag')}</AddOnTag>
            <AddOnTitle>{t('pricing.production.title')}</AddOnTitle>
            <AddOnSubtitle>{t('pricing.production.subtitle')}</AddOnSubtitle>

            <AddOnGrid>
              {productionFeatures.map((feat, idx) => (
                <AddOnFeatureCard key={idx}>
                  <AddOnFeatureIcon>{feat.icon}</AddOnFeatureIcon>
                  <AddOnFeatureTitle>{t(`pricing.production.features.${feat.titleKey}`)}</AddOnFeatureTitle>
                  <AddOnFeatureDesc>{t(`pricing.production.features.${feat.descKey}`)}</AddOnFeatureDesc>
                </AddOnFeatureCard>
              ))}
            </AddOnGrid>

            <AddOnCTA>
              <AddOnCTAText>
                <h3>{t('pricing.production.ctaTitle')}</h3>
                <p>{t('pricing.production.ctaText')}</p>
              </AddOnCTAText>
              <AddOnCTAButton to={`/${lang}/demo`}>
                {t('pricing.production.ctaButton')} <FaArrowRight />
              </AddOnCTAButton>
            </AddOnCTA>
          </AddOnContainer>
        </AddOnSection>

        {/* ROI BLOCK */}
        <ROISection>
          <ROIContainer>
            <ROITitle>{t('pricing.roi.title')}</ROITitle>
            <ROISubtitle>{t('pricing.roi.subtitle')}</ROISubtitle>
            <ROIList>
              <ROIItem><FaCheck /> {t('pricing.roi.item1')}</ROIItem>
              <ROIItem><FaCheck /> {t('pricing.roi.item2')}</ROIItem>
              <ROIItem><FaCheck /> {t('pricing.roi.item3')}</ROIItem>
              <ROIItem><FaCheck /> {t('pricing.roi.item4')}</ROIItem>
            </ROIList>
            <ROICTAButton to={`/${lang}/demo`}>
              {t('pricing.roi.ctaButton')} <FaArrowRight />
            </ROICTAButton>
          </ROIContainer>
        </ROISection>

        {/* COMPARISON TABLE */}
        <ComparisonSection>
          <SectionTitle>{t('pricing.comparison.title')}</SectionTitle>
          <SectionSubtitle>{t('pricing.comparison.subtitle')}</SectionSubtitle>

          <TableWrapper>
            <ComparisonTable>
              <thead>
                <tr>
                  <th>{t('pricing.comparison.featureHeader')}</th>
                  <th>Starter</th>
                  <th>Growth</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{f(row.key)}</td>
                    <td>{row.starter ? <CheckMark><FaCheckCircle /></CheckMark> : <DashMark />}</td>
                    <td>{row.growth ? <CheckMark><FaCheckCircle /></CheckMark> : <DashMark />}</td>
                    <td>{row.enterprise ? <CheckMark><FaCheckCircle /></CheckMark> : <DashMark />}</td>
                  </tr>
                ))}
              </tbody>
            </ComparisonTable>
          </TableWrapper>
        </ComparisonSection>

        {/* GUARANTEE */}
        <GuaranteeSection>
          <GuaranteeGrid>
            <GuaranteeCard>
              <FaRocket />
              <h3>{t('pricing.guarantee.setup.title')}</h3>
              <p>{t('pricing.guarantee.setup.description')}</p>
            </GuaranteeCard>
            <GuaranteeCard>
              <FaUserTie />
              <h3>{t('pricing.guarantee.manager.title')}</h3>
              <p>{t('pricing.guarantee.manager.description')}</p>
            </GuaranteeCard>
            <GuaranteeCard>
              <FaHeadset />
              <h3>{t('pricing.guarantee.support.title')}</h3>
              <p>{t('pricing.guarantee.support.description')}</p>
            </GuaranteeCard>
          </GuaranteeGrid>
        </GuaranteeSection>

        {/* FAQ */}
        <FAQSection>
          <FAQContainer>
            <FAQTitle>{t('pricing.faq.title')}</FAQTitle>
            {faqItems.map((item, index) => (
              <FAQItem key={index} $open={openFaq === index}>
                <FAQQuestion $open={openFaq === index} onClick={() => toggleFaq(index)}>
                  {item.q}
                  <FaChevronDown />
                </FAQQuestion>
                <FAQAnswer $open={openFaq === index}>
                  <p>{item.a}</p>
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </FAQSection>

        {/* CTA */}
        <CTASection>
          <CTAContent>
            <CTATitle>{t('pricing.cta.title')}</CTATitle>
            <CTAText>{t('pricing.cta.text')}</CTAText>
            <CTAButtons>
              <CTAPrimary to={`/${lang}/demo`}>
                {t('pricing.cta.primaryButton')} <FaArrowRight />
              </CTAPrimary>
            </CTAButtons>
          </CTAContent>
        </CTASection>

        {/* SETUP NOTE */}
        <SetupNote>
          <p>{t('pricing.setupNote.line1')}</p>
          <p>{t('pricing.setupNote.line2')}</p>
          <p>{t('pricing.setupNote.line3')}</p>
          <p>{t('pricing.setupNote.line4')}</p>
        </SetupNote>
      </PageContainer>
    </>
  );
};

export default PricingPage;
